import { pickT } from "@repo/shared";
import { FLOW_STATUSES, type FlowStatus } from "@repo/shared/types";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";
import { T } from "./flow-status-toggle.i18n";

type FlowStatusToggleProps = {
  value: FlowStatus;
  onChange: (status: FlowStatus) => void;
  language: string;
  disabled?: boolean;
  /** Accessible name — the flow's name in a table row, the card title in settings. */
  ariaLabel: string;
  size?: "sm" | "default";
};

/**
 * QL-239 — Inactive / Preview / Active. The server validates a move into
 * active and explains a refusal; this only reports the designer's choice.
 */
export function FlowStatusToggle({
  value,
  onChange,
  language,
  disabled,
  ariaLabel,
  size = "default",
}: FlowStatusToggleProps) {
  const t = pickT(T, language);
  return (
    <ToggleGroup
      type="single"
      value={value}
      // Radix single mode emits "" when the active item is clicked again.
      onValueChange={(next) => {
        if (!next || next === value) return;
        onChange(next as FlowStatus);
      }}
      disabled={disabled}
      aria-label={ariaLabel}
      className="w-fit"
    >
      {FLOW_STATUSES.map((status) => (
        <ToggleGroupItem
          key={status}
          value={status}
          // The compact table variant keeps the standard h-10 / h-8 trough pair
          // (DESIGN.md control heights) and only narrows the horizontal padding,
          // so the longer nb_NO label ("Forhåndsvisning") fits the column.
          className={cn(size === "sm" && "px-2")}
        >
          {t[status]}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}
